import React, { FormEvent, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ArrowRight, KeyRound, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react';
import { functions } from '../services/firebase';
import Logo from './Logo';

type AccessResponse = {
  authorized: boolean;
  accessToken?: string;
  expiresAt?: number;
};

type GateStatus = 'checking' | 'locked' | 'authorized';

const SESSION_KEY = 'clero:business-preview:nolanking';

const AccessShell = ({ children }: { children: React.ReactNode }) => (
  <main className="relative grid min-h-screen place-items-center overflow-hidden bg-zinc-950 px-5 py-12 text-zinc-950">
    <div aria-hidden="true" className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
    <div aria-hidden="true" className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
    <div className="relative w-full max-w-md rounded-[32px] border border-white/15 bg-white p-7 shadow-2xl shadow-black/40 sm:p-9">
      {children}
    </div>
  </main>
);

const GateBrand = ({ username }: { username?: string }) => {
  const isDefault = !username || username.toLowerCase() === 'nolanking';
  const subtitle = isDefault ? 'Nolan King preview' : `${username} preview`;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="rounded-2xl bg-zinc-100 p-2.5"><Logo size="xs" /></div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[.22em] text-teal-700">Clero Business</p>
          <p className="mt-0.5 font-black text-zinc-950">{subtitle}</p>
        </div>
      </div>
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-zinc-950 text-white"><LockKeyhole className="h-5 w-5" /></div>
    </div>
  );
};

const getErrorMessage = (error: any) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  if (code.includes('not-found') || message.includes('not found') || message.includes('NOT_FOUND')) {
    return 'Business preview not found.';
  }
  if (code.includes('resource-exhausted')) return 'Too many attempts. Wait 15 minutes and try again.';
  if (code.includes('permission-denied')) return 'That passcode is not correct.';
  if (code.includes('failed-precondition')) return 'Preview access is not configured yet.';
  return 'Could not verify this passcode. Try again.';
};

const BusinessPasscodeGate = ({ username, children }: { username: string; children: React.ReactNode }) => {
  const [status, setStatus] = useState<GateStatus>('checking');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const verifyAccess = httpsCallable<{
    username: string;
    passcode?: string;
    accessToken?: string;
  }, AccessResponse>(functions, 'verifyBusinessPreviewAccess');

  useEffect(() => {
    let cancelled = false;
    const token = window.sessionStorage.getItem(SESSION_KEY) || '';
    if (!token) {
      setStatus('locked');
      return () => { cancelled = true; };
    }

    void verifyAccess({ username, accessToken: token })
      .then((result) => {
        if (cancelled) return;
        if (result.data?.authorized) setStatus('authorized');
        else {
          window.sessionStorage.removeItem(SESSION_KEY);
          setStatus('locked');
        }
      })
      .catch((checkErr) => {
        if (cancelled) return;
        // Do not clear token from sessionStorage on transient server / network failures
        setError(getErrorMessage(checkErr));
        setStatus('locked');
      });

    return () => { cancelled = true; };
  }, [username]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!passcode.trim() || isWorking) return;
    setError('');
    setIsWorking(true);
    try {
      const result = await verifyAccess({ username, passcode });
      if (!result.data?.authorized || !result.data.accessToken) throw new Error('Access denied');
      window.sessionStorage.setItem(SESSION_KEY, result.data.accessToken);
      setPasscode('');
      setStatus('authorized');
    } catch (verificationError: any) {
      setError(getErrorMessage(verificationError));
    } finally {
      setIsWorking(false);
    }
  };

  const retrySavedToken = async () => {
    const token = window.sessionStorage.getItem(SESSION_KEY) || '';
    if (!token || isWorking) return;
    setError('');
    setIsWorking(true);
    try {
      const result = await verifyAccess({ username, accessToken: token });
      if (result.data?.authorized) {
        setStatus('authorized');
      } else {
        window.sessionStorage.removeItem(SESSION_KEY);
        setError('Saved access expired. Please enter the passcode.');
      }
    } catch (checkErr: any) {
      setError(getErrorMessage(checkErr));
    } finally {
      setIsWorking(false);
    }
  };

  if (status === 'authorized') return <>{children}</>;

  if (status === 'checking') {
    return <AccessShell><div className="py-8 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-teal-600" /><h1 className="mt-5 text-xl font-black">Opening preview…</h1><p className="mt-2 text-sm font-medium text-zinc-500">Checking this browser’s access</p></div></AccessShell>;
  }

  return <AccessShell>
    <GateBrand username={username} />
    <div className="mt-8">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-50 text-teal-700"><ShieldCheck className="h-7 w-7" /></div>
      <h1 className="mt-5 text-3xl font-black tracking-tight">Enter preview passcode.</h1>
      <p className="mt-3 text-sm font-medium leading-6 text-zinc-600">
        Use the passcode shared with you to view {username?.toLowerCase() === 'nolanking' ? 'Nolan King Training’s' : `${username}’s`} Clero Business preview.
      </p>
    </div>
    <form onSubmit={submit} className="mt-7">
      <label htmlFor="business-preview-passcode" className="text-sm font-extrabold text-zinc-800">Passcode</label>
      <div className="relative mt-2"><KeyRound className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400" /><input id="business-preview-passcode" autoFocus type="password" autoComplete="current-password" value={passcode} onChange={(event) => setPasscode(event.target.value)} placeholder="Enter passcode" className="h-14 w-full rounded-2xl border border-zinc-300 pl-12 pr-4 text-base font-bold outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-600/10" /></div>
      <button type="submit" disabled={!passcode.trim() || isWorking} className="mt-4 inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-teal-600 font-extrabold text-white transition hover:bg-teal-700 disabled:opacity-40">{isWorking ? <><Loader2 className="h-5 w-5 animate-spin" /> Checking…</> : <>Open preview <ArrowRight className="h-5 w-5" /></>}</button>
      {typeof window !== 'undefined' && Boolean(window.sessionStorage.getItem(SESSION_KEY)) && (
        <button
          type="button"
          onClick={retrySavedToken}
          disabled={isWorking}
          className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 text-sm font-bold text-zinc-800 transition hover:bg-zinc-100 disabled:opacity-40"
        >
          {isWorking ? <Loader2 className="h-4 w-4 animate-spin text-zinc-600" /> : 'Retry saved preview access'}
        </button>
      )}
    </form>
    {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm font-semibold text-rose-700">{error}</p>}
    <p className="mt-6 text-center text-xs font-semibold text-zinc-600">Access stays active for this browser tab.</p>
  </AccessShell>;
};

export default BusinessPasscodeGate;
